import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import ButtonComponent from "../../components/ButtonComponent/ButtonComponent";
import CheckboxComponent from "../../components/CheckboxComponent/CheckboxComponent";
import DropdownComponent from "../../components/DropdownComponent/DropdownComponent";
import EditorComponent from "../../components/EditorComponent/EditorComponent";
import InputComponent from "../../components/InputComponent/InputComponent";
import LoaderComponent from "../../components/LoaderComponent/LoaderComponent";
import NotificationComponent from "../../components/NotificationComponent/NotificationComponent";
import RadioboxComponent from "../../components/RadioboxComponent/RadioboxComponent";
import { addEditAboutEvent, addUpdateEvent, getAboutEvent, getEventList, getImagesList } from "../../services/apiController";
import './AboutEvent.css';
import UploadComponent from "../../components/UploadComponent/UploadComponent";
import { countryCodeOptions } from "../../utils/countryCodeOptions";

const AboutEvent = () => {
    // #region States
    const { state } = useLocation();
    const navigate = useNavigate();

    const isEditMode = Boolean(state?.eventMasterId);

    const [loading, setLoading] = useState(false);
    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };

    const [errors, setErrors] = useState({}); // To store validation errors
    const [formData, setFormData] = useState({
        eventName: '',
        eventDate: '',
        eventDay: '',
        eventTime: '',
        eventvenue: '',
        country: '',
        countryCode: '',
        currency: '',
        enquiryToEmailId: '',
        bccEmailId: '',
        ticketMasterId: 0,
        maxLimit: 0,
        platformFee: 'no',
        eventPostpone: 'no',
        eventClose: 'no',

        image1Name: "",
        image2Name: "",
        image3Name: "",
        image4Name: "",
        image5Name: "",
        image6Name: "",

        image1: "",
        image2: "",
        image3: "",
        image4: "",
        image5: "",
        image6: "",

        eventAboutInfo: '',
        eventAboutFAQ: '',
        eventAboutTermsConditions: '',
        eventAboutOfferStatus: false,
        eventAboutOfferContent: ''
    });

    const [imageChanged, setImageChanged] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false,
        image5: false,
        image6: false,
    });
    //#endregion

    useEffect(() => {
        if (state?.eventMasterId) {
            wsGetEventData(state.eventMasterId);
        }
    }, [state?.eventMasterId]);

    // #region API Methods
    const wsGetEventData = (eventMasterId) => {
        setLoading(true);

        getEventList()
            .then((response) => {
                const tickets = response?.tickets || [];
                if (eventMasterId) {
                    const selectedEvent = tickets.find(item => item.TicketMasterId === Number(eventMasterId));
                    if (selectedEvent) {
                        setEventFormData(selectedEvent);
                        wsGetAboutEvent(selectedEvent.TicketMasterId);
                    }
                }
                setLoading(false);
            }).catch((error) => {
                setLoading(false);
            });
    };

    const getEventDay = (date) => {
        if (!date) return '';

        const d = new Date(`${date}T00:00:00`);

        return d.toLocaleDateString('en-US', {
            weekday: 'long'
        });
    };

    const formatEventDate = (date) => {
        if (!date) return '';

        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }

        const d = new Date(date);

        return d.toISOString().split('T')[0];
    };

    const formatEventTime = (time) => {
        if (!time) return '';

        // Already in HH:mm format
        if (/^\d{2}:\d{2}$/.test(time)) {
            return time;
        }

        // Convert "7 PM", "7:30 PM", etc.
        const match = time.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);

        if (!match) return '';

        let hours = parseInt(match[1], 10);
        const minutes = match[2] || '00';
        const period = match[3].toUpperCase();

        if (period === 'PM' && hours !== 12) {
            hours += 12;
        }
        if (period === 'AM' && hours === 12) {
            hours = 0;
        }
        return `${String(hours).padStart(2, '0')}:${minutes}`;
    };

    const setEventFormData = (event) => {
        setFormData(prev => ({
            ...prev,
            eventName: event.EventName || '',
            eventDate: formatEventDate(event.EventDate),
            eventDay: event.EventDay || '',
            eventTime: formatEventTime(event.EventTime),
            eventvenue: event.Venue || '',
            country: event.Country || '',
            countryCode: event.CountryCode || '',
            currency: event.Currency || '',
            enquiryToEmailId: event.EnquiryToEmailId || '',
            bccEmailId: event.BCCEmailId || '',
            ticketMasterId: event.TicketMasterId || 0,
            maxLimit: event.MaxLimit || 0,
            eventPostpone: event.EventPostpone ? 'yes' : 'no',
            eventClose: event.EventClose ? 'yes' : 'no'
        }));
    };

    const wsGetAboutEvent = (TicketMasterId) => {
        getAboutEvent({ TicketMasterId: TicketMasterId })
            .then((response) => {
                setFormData(prev => ({
                    ...prev,
                    eventAboutFAQ: response.EventAboutFAQ || '',
                    eventAboutInfo: response.EventAboutInfo || '',
                    eventAboutTermsConditions: response.EventAboutTermsConditions || '',
                    eventAboutOfferStatus: response.EventAboutOfferStatus ?? false,
                    eventAboutOfferContent: response.EventAboutOfferContent || ''
                }));

                wsGetImagesList(TicketMasterId);

            }).catch((error) => {
                setLoading(false);
            });
    };

    const wsGetImagesList = (eventId) => {
        getImagesList({ ticket_master_id: eventId })
            .then((response) => {
                const images = response?.images || {};

                setFormData(prev => ({
                    ...prev,

                    image1Name: images.image1 ? images.image1.split('/').pop() : "",
                    image2Name: images.image2 ? images.image2.split('/').pop() : "",
                    image3Name: images.image3 ? images.image3.split('/').pop() : "",
                    image4Name: images.image4 ? images.image4.split('/').pop() : "",
                    image5Name: images.image5 ? images.image5.split('/').pop() : "",
                    image6Name: images.image6 ? images.image6.split('/').pop() : "",

                    image1: images.image1 || "",
                    image2: images.image2 || "",
                    image3: images.image3 || "",
                    image4: images.image4 || "",
                    image5: images.image5 || "",
                    image6: images.image6 || "",
                }));

                setImageChanged({
                    image1: false,
                    image2: false,
                    image3: false,
                    image4: false,
                    image5: false,
                    image6: false,
                });

                setLoading(false);
            }).catch((err) => {
                setLoading(false);
            });
    }
    //#endregion

    // #region Handle Methods
    const validateForm = () => {
        const newErrors = {};

        if (!formData.eventName.trim()) {
            newErrors.eventName = "Please Enter Event Name";
        }

        if (!formData.eventvenue.trim()) {
            newErrors.eventvenue = "Please enter event venue";
        }

        if (!formData.eventDate) {
            newErrors.eventDate = "Please select event date";
        }

        if (!formData.eventTime) {
            newErrors.eventTime = "Please select event time";
        }

        if (!formData.eventDay) {
            newErrors.eventDay = "Please select event day";
        }

        if (!formData.country) {
            newErrors.country = "Please select country";
        }

        if (!formData.currency) {
            newErrors.currency = "Please select currency";
        }

        if (!formData.enquiryToEmailId.trim()) {
            newErrors.enquiryToEmailId = "Please enter enquiry email";
        }

        if (!formData.bccEmailId.trim()) {
            newErrors.bccEmailId = "Please enter BCC email";
        }

        if (!formData.maxLimit || Number(formData.maxLimit) <= 0) {
            newErrors.maxLimit = "Please enter maximum limit";
        }

        if (!formData.eventAboutInfo.trim()) {
            newErrors.eventAboutInfo = "Please enter event about details";
        }

        if (!formData.eventAboutFAQ.trim()) {
            newErrors.eventAboutFAQ = "Please enter FAQ";
        }

        if (!formData.eventAboutTermsConditions.trim()) {
            newErrors.eventAboutTermsConditions = "Please enter terms and conditions";
        }

        if (formData.eventAboutOfferStatus && !formData.eventAboutOfferContent.trim()) {
            newErrors.eventAboutOfferContent = "Please enter offer content";
        }
        setErrors(newErrors);

        // Focus first error field
        const firstErrorField = Object.keys(newErrors)[0];

        if (firstErrorField) {
            const element = document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
                element.focus();
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e, type, maxLength) => {
        const { name, value } = e.target;

        // setFormData((prev) => ({ ...prev, [name]: value }));

        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'eventDate' && {
                eventDay: getEventDay(value)
            })
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleImageUpload = (e, imageNumber) => {
        const file = e.target.files?.[0];

        if (!file) return;

        const reader = new FileReader();

        reader.onloadend = () => {
            setFormData(prev => ({
                ...prev,
                [`image${imageNumber}Name`]: file.name,
                [`image${imageNumber}`]: reader.result
            }));

            setImageChanged(prev => ({
                ...prev,
                [`image${imageNumber}`]: true
            }));
        };

        reader.readAsDataURL(file);
    };

    const handleAddEditAboutUs = async () => {

        if (!validateForm()) {
            return;
        }

        // API CALL
        setLoading(true);

        try {
            // If edit -> existing ID
            // If add -> 0
            const ticketMasterId = state?.eventMasterId ? Number(state.eventMasterId) : 0;

            // 1. ADD / UPDATE EVENT MASTER
            const imagePayload = {};

            for (let i = 1; i <= 6; i++) {
                const imageKey = `image${i}`;
                const imageNameKey = `image${i}Name`;

                // if (imageChanged[imageKey]) {
                //     imagePayload[imageKey] = formData[imageKey];
                //     imagePayload[imageNameKey] = formData[imageNameKey];
                // }

                if (imageChanged[imageKey]) {
                    imagePayload[imageKey.charAt(0).toUpperCase() + imageKey.slice(1)] = formData[imageKey];
                    imagePayload[imageNameKey.charAt(0).toUpperCase() + imageNameKey.slice(1)] = formData[imageNameKey];
                }
            }

            const eventParameter = {
                EventName: formData.eventName,
                EventDate: formData.eventDate,
                EventDay: formData.eventDay,
                EventTime: formData.eventTime,
                Venue: formData.eventvenue,
                Country: formData.country,
                CountryCode: formData.countryCode,
                Currency: formData.currency,
                EnquiryToEmailId: formData.enquiryToEmailId,
                BCCEmailId: formData.bccEmailId,

                TicketMasterId: ticketMasterId,

                MaxLimit: Number(formData.maxLimit),

                EventPostpone: formData.eventPostpone === "yes",
                EventClose: formData.eventClose === "yes",

                ...imagePayload
            };

            // ADD / UPDATE EVENT API CALL
            const eventResponse = await addUpdateEvent(eventParameter);

            if (!eventResponse?.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: eventResponse?.message || "Unable to save event", snackbarSeverity: "error" });
                setLoading(false);
                return;
            }

            // GET ID FROM MASTER API
            const savedTicketMasterId = eventResponse?.TicketMasterId || eventResponse?.ticketMasterId || ticketMasterId;

            if (!savedTicketMasterId) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Event ID not found", snackbarSeverity: "error" });
                setLoading(false);
                return;
            }

            // 2. ADD / UPDATE ABOUT EVENT
            const aboutEventParameter = {
                TicketMasterId: savedTicketMasterId,
                EventAboutInfo: formData.eventAboutInfo,
                EventAboutFAQ: formData.eventAboutFAQ,
                EventAboutTermsConditions: formData.eventAboutTermsConditions,
                EventAboutOfferStatus: formData.eventAboutOfferStatus,
                EventAboutOfferContent: formData.eventAboutOfferContent
            };

            // ADD / UPDATE ABOUT EVENT API CALL
            const aboutResponse = await addEditAboutEvent(aboutEventParameter);

            if (aboutResponse?.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: aboutResponse.message || "Event saved successfully", snackbarSeverity: "success" });
                setTimeout(() => {
                    navigate('/event-list');
                }, 2000);
            } else {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: aboutResponse?.message || "Event saved but About Event failed", snackbarSeverity: "error" });
            }

        } catch (error) {
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Something went wrong", snackbarSeverity: "error" });
        } finally {
            setLoading(false);
        }
    }
    //#endregion

    // #region UI Methods    
    return (loading ? <LoaderComponent />
        : <React.Fragment>
            <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />

            <div className="booking-container">
                <p><strong>About Event</strong></p>
                <div style={{ marginTop: '20px' }}>
                    <label>Event Name*</label>
                    <InputComponent name="eventName" placeholder="Event Name*" value={formData.eventName} onChange={handleChange} error={errors.eventName} />
                </div>

                <div style={{ marginTop: '20px' }}>
                    <label>Event Venue*</label>
                    <InputComponent name="eventvenue" placeholder="Event Venue*" value={formData.eventvenue} onChange={handleChange} error={errors.eventvenue} />
                </div>

                <div style={{ marginTop: '20px' }}>
                    <label>Max Ticket Limit*</label>
                    <InputComponent name="maxLimit" placeholder="Max Ticket Limit*" value={formData.maxLimit} onChange={handleChange} error={errors.maxLimit} />
                </div>

                <div className="form-three-grid">
                    <div style={{ marginTop: '20px' }}>
                        <label>Event Date*</label>
                        <InputComponent type='date' name="eventDate" placeholder="Event Date*" value={formData.eventDate} onChange={handleChange} error={errors.eventDate} />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <label>Event Time*</label>
                        <InputComponent type='time' name="eventTime" placeholder="Event Time*" value={formData.eventTime} onChange={handleChange} error={errors.eventTime} />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <label>Event Day*</label>
                        <InputComponent disabled name="eventDay" placeholder="Event Day*" value={formData.eventDay} error={errors.eventDay} />
                    </div>
                </div>

                <div className="form-three-grid">
                    <div style={{ marginTop: '20px' }}>
                        <label>Country*</label>
                        {/* <InputComponent name="country" placeholder="Country*" value={formData.country} onChange={handleChange} error={errors.country} /> */}

                        <DropdownComponent name="country" placeholder="Country*" value={formData.country} error={errors.country}
                            options={countryCodeOptions.map(item => ({ id: item.country, value: item.country }))}
                            onChange={(e) => {
                                const selectedCountry = countryCodeOptions.find(item => item.country === e.target.value);

                                setFormData(prev => ({
                                    ...prev,
                                    country: e.target.value,
                                    countryCode: selectedCountry?.id || '',
                                    currency: selectedCountry?.currency || ''
                                }));
                            }}
                        />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <label>Country Code*</label>
                        <InputComponent name="countryCode" placeholder="Country Code*" value={formData.countryCode} onChange={handleChange} error={errors.countryCode} />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <label>Currency*</label>
                        <InputComponent name="currency" placeholder="Currency*" value={formData.currency} onChange={handleChange} error={errors.currency} />
                    </div>
                </div>

                <div className="form-grid">
                    <div style={{ marginTop: '20px' }}>
                        <label>Enquiry To Email*</label>
                        <InputComponent type="email" name="enquiryToEmailId" placeholder="Enquiry To Email*" value={formData.enquiryToEmailId} onChange={handleChange} error={errors.enquiryToEmailId} />
                    </div>
                    <div style={{ marginTop: '20px' }}>
                        <label>BCC Email*</label>
                        <InputComponent type="email" name="bccEmailId" placeholder="BCC Email*" value={formData.bccEmailId} onChange={handleChange} error={errors.bccEmailId} />
                    </div>
                </div>

                {/* event platform fee collect from organizer or customer side  */}
                {/* <div style={{ marginTop: '20px', display: 'flex' }}>
                    <label style={{ width: '100%' }}>Event platform fee collect from ?</label>
                    <RadioboxComponent
                        uniqueId="platformFee-yes"
                        name="platformFee"
                        value="yes"
                        checked={formData.platformFee === "yes"}
                        onChange={(e) => setFormData(prev => ({ ...prev, platformFee: e.target.value }))}
                        label={<label htmlFor="platformFee-yes" className="event-radio-card-label">
                            <span className="event-radio-card-text">Organizer</span>
                        </label>}
                    />

                    <RadioboxComponent
                        uniqueId="platformFee-no"
                        name="platformFee"
                        value="no"
                        checked={formData.platformFee === "no"}
                        onChange={(e) => setFormData(prev => ({ ...prev, platformFee: e.target.value }))}
                        label={<label htmlFor="platformFee-no" className="event-radio-card-label">
                            <span className="event-radio-card-text">Customer</span>
                        </label>}
                    />
                </div> */}

                <div style={{ marginTop: '20px', display: 'flex' }}>
                    <label style={{ width: '100%' }}>Is Event Postpone ?</label>

                    <RadioboxComponent
                        uniqueId="uniqueId-yes"
                        name="eventPostpone"
                        value="yes"
                        checked={formData.eventPostpone === "yes"}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventPostpone: e.target.value }))}
                        label={<label htmlFor="uniqueId-yes" className="event-radio-card-label">
                            <span className="event-radio-card-text">Yes</span>
                        </label>}
                    />

                    <RadioboxComponent
                        uniqueId="uniqueId-no"
                        name="eventPostpone"
                        value="no"
                        checked={formData.eventPostpone === "no"}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventPostpone: e.target.value }))}
                        label={<label htmlFor="uniqueId-no" className="event-radio-card-label">
                            <span className="event-radio-card-text">No</span>
                        </label>}
                    />
                </div>

                <div style={{ marginTop: '20px', display: 'flex' }}>
                    <label style={{ width: '100%' }}>Is Event Close ?</label>

                    <RadioboxComponent
                        uniqueId="close-yes"
                        name="eventClose"
                        value="yes"
                        checked={formData.eventClose === "yes"}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventClose: e.target.value }))}
                        label={<label htmlFor="close-yes" className="event-radio-card-label">
                            <span className="event-radio-card-text">Yes</span>
                        </label>}
                    />

                    <RadioboxComponent
                        uniqueId="close-no"
                        name="eventClose"
                        value="no"
                        checked={formData.eventClose === "no"}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventClose: e.target.value }))}
                        label={<label htmlFor="close-no" className="event-radio-card-label">
                            <span className="event-radio-card-text">No</span>
                        </label>}
                    />
                </div>

                <div className="form-three-grid" style={{ marginTop: '20px', }}>
                    {/* <div style={{ display: 'grid' }}>
                        <label>Banner Image</label>
                        <UploadComponent label="Upload Image 1" accept=".jpeg,.jpg,.png" preview={formData.image1} onChange={(e) => handleImageUpload(e, 1)} />
                    </div> */}
                    <div style={{ display: 'grid' }}>
                        <label>Upload Banner Image</label>
                        <UploadComponent label="Upload Image (1920px * 500px)" accept=".jpeg,.jpg,.png" preview={formData.image2} onChange={(e) => handleImageUpload(e, 2)} />
                    </div>
                    {/* <div style={{ display: 'grid' }}>
                        <label>Banner Image</label>
                        <UploadComponent label="Upload Image 3" accept=".jpeg,.jpg,.png" preview={formData.image3} onChange={(e) => handleImageUpload(e, 3)} />
                    </div> */}
                    {/* <div style={{ display: 'grid' }}>
                        <label>Banner Image</label>
                        <UploadComponent label="Upload Image 4" accept=".jpeg,.jpg,.png" preview={formData.image4} onChange={(e) => handleImageUpload(e, 4)} />
                    </div> */}
                    <div style={{ display: 'grid' }}>
                        <label>Ticket Image</label>
                        <UploadComponent label="Upload Image (300px * 530px)" accept=".jpeg,.jpg,.png" preview={formData.image5} onChange={(e) => handleImageUpload(e, 5)} />
                    </div>
                    {/* <div style={{ display: 'grid' }}>
                        <label>Banner Image</label>
                        <UploadComponent label="Upload Image 6" accept=".jpeg,.jpg,.png" preview={formData.image6} onChange={(e) => handleImageUpload(e, 6)} />
                    </div> */}
                </div>

                <div style={{ marginTop: '20px' }}>
                    <label>Event Info</label>
                    <EditorComponent id={"eventAboutInfo"} height={300} placeholder="Event Info*" value={formData.eventAboutInfo} onChange={(value) => setFormData({ ...formData, eventAboutInfo: value })} />
                    {errors?.eventAboutInfo && <span className="error-text">{errors?.eventAboutInfo}</span>}
                </div>

                <div style={{ marginTop: '20px' }}>
                    <label>Event FAQ</label>
                    <EditorComponent id={"eventAboutFAQ"} height={300} placeholder="Event FAQ*" value={formData.eventAboutFAQ} onChange={(value) => setFormData({ ...formData, eventAboutFAQ: value })} />
                    {errors?.eventAboutFAQ && <span className="error-text">{errors?.eventAboutFAQ}</span>}
                </div>

                <div style={{ marginTop: '20px' }}>
                    <label>Event Terms & Conditions</label>
                    <EditorComponent id={"eventAboutTermsConditions"} height={300} placeholder="Event Terms & Conditions*" value={formData.eventAboutTermsConditions} onChange={(value) => setFormData({ ...formData, eventAboutTermsConditions: value })} />
                    {errors?.eventAboutTermsConditions && <span className="error-text">{errors?.eventAboutTermsConditions}</span>}
                </div>

                <div style={{ marginTop: '20px', display: 'none' }}>
                    <CheckboxComponent id={"eventAboutOfferStatus"} label="Offer is Active?" checked={formData.eventAboutOfferStatus} onChange={(e) => setFormData({ ...formData, eventAboutOfferStatus: e.target.checked })} />
                    <label>Offer Content</label>
                    <EditorComponent disabled={!formData.eventAboutOfferStatus} id={"eventAboutOfferContent"} height={300} placeholder="Offer Content*" value={formData.eventAboutOfferContent} onChange={(value) => setFormData({ ...formData, eventAboutOfferContent: value })} />
                    {errors?.eventAboutOfferStatus && <span className="error-text">{errors?.eventAboutOfferStatus}</span>}
                </div>

                <div style={{ textAlign: 'end', marginTop: '2%' }}>
                    <ButtonComponent disabled={loading} onClick={handleAddEditAboutUs}>{isEditMode ? 'Update' : 'Add'}</ButtonComponent>
                </div>

            </div>
        </React.Fragment>)
    //#endregion
}
export default AboutEvent;